// Client speech contracts: BUILD 1 contract C-7, plus the shapes of the C-3
// speech routes that more than one client package reads.
//
// Written by WP-0b and frozen after the b1-contracts tag. WP-3 implements
// these (capture core, mic owner, spool, mint, socket); WP-4
// consumes them for Record in Chat and the live preview. Changes go through
// WP-INT as a contract change request.
//
// BUILD 2 (WP2-0b, frozen after the b2-contracts tag) adds, additively: the
// `voice` and `enrolment` capture purposes and mic kinds, the voice-mode mint
// and its 503, and the `voice`, `handsFree` and `readAloud` availability modes
// (C2-6, C2-8). Voice-only shapes live in ui/voice/contracts.ts.
import type { ReadAloudConfig, VoiceConfig, VoiceStyle } from '@/voice/contracts';
import type {
  ClientControl,
  ServerMessage,
  SpeechErrorCode,
  SpeechLanguageMode,
} from './transport/protocol';

// ── Capture (ui/speech/capture/core.ts: startCapture) ───────────────────────

export type PcmFrame = {
  seq: number;
  sampleOffset: number;
  samples: number;
  /** Int16LE mono 16 kHz. */
  pcm: ArrayBuffer;
};
export type CaptureSource = 'mic' | 'tab' | 'system';
export type CaptureLifecycle =
  | 'mic-ended'
  | 'tab-ended'
  | 'system-ended'
  | 'muted'
  | 'unmuted'
  | 'hidden'
  | 'visible'
  | 'context-suspended'
  | 'device-changed';
export type CaptureErrorCode =
  | 'permission_denied'
  | 'native_permission_denied'
  | 'native_unavailable'
  | 'no_microphone'
  | 'microphone_busy'
  | 'insecure_context'
  | 'share_cancelled'
  | 'share_no_audio'
  | 'tab_unsupported'
  | 'unsupported';
/** Informational; the capture core treats every purpose alike. */
export type CapturePurpose = 'dictation' | 'meeting' | 'voice' | 'enrolment';
export interface CaptureOptions {
  sources: CaptureSource[];
  purpose: CapturePurpose;
  deviceId?: string;
  /** Explicit OS loopback/monitor input; never falls back to the microphone. */
  systemDeviceId?: string;
  onFrame(f: PcmFrame): void;
  /** Optional low-latency activity level for voice barge-in only. */
  onActivity?(level: number, samples?: Float32Array, sampleOffset?: number): void;
  onLevel?(level: number): void;
  /** Independent inputs, before mixing, so missing meeting audio is visible. */
  onSourceLevels?(levels: Partial<Record<CaptureSource, number>>): void;
  onLifecycle?(e: CaptureLifecycle): void;
}
export interface CaptureHandle {
  readonly startedAt: number;
  readonly sources: CaptureSource[];
  stop(): Promise<{ lastSeq: number; totalSamples: number }>;
}
/** Rejects with `CaptureError`. */
export type StartCapture = (o: CaptureOptions) => Promise<CaptureHandle>;

/**
 * The AudioWorklet processor registered by ui/speech/capture/pcm-capture.worklet.ts,
 * which the build emits as `assets/pcm-capture.worklet.js` beside `assets/app.js`.
 */
export const PCM_CAPTURE_PROCESSOR = 'poneva-pcm-capture';
export const PCM_CAPTURE_WORKLET_ASSET = 'pcm-capture.worklet.js';

/** The one rejection type of `startCapture`; callers map `code` to copy. */
export class CaptureError extends Error {
  readonly code: CaptureErrorCode;
  constructor(code: CaptureErrorCode) {
    super(code);
    this.name = 'CaptureError';
    this.code = code;
  }
}

// ── Microphone ownership (ui/speech/capture/mic-owner.ts: micOwner) ─────────

export type MicKind = 'dictation' | 'voice' | 'meeting' | 'enrolment';
/**
 * A meeting or a voice enrolment refuses every claim; any other claim
 * pre-empts dictation or voice (the latest tap wins).
 */
export interface MicOwner {
  claim(kind: MicKind, onPreempt: () => void): (() => void) | null;
  current(): MicKind | null;
  subscribe(cb: (k: MicKind | null) => void): () => void;
}

// ── Device spool (ui/speech/spool/index.ts: speechSpool) ────────────────────

/**
 * Voice mode and enrolment never spool: voice keeps the open turn's frames in
 * memory, and enrolment audio never leaves memory before its upload.
 */
export type SpoolMeta = {
  id: string;
  owner: string;
  kind: 'dictation' | 'meeting';
  /** Dictation's original chat; kept across retries, never reassigned. */
  threadId?: string;
  meetingId?: string;
  take?: number;
  createdAt: number;
  updatedAt: number;
  state: 'capturing' | 'stopped' | 'committed' | 'failed';
  lastSeq: number;
  samples: number;
  errorCode?: string;
  /** Original meeting chunk encoding and matching acknowledgement, when retained locally. */
  meetingChunks?: Record<
    number,
    { final: boolean; sha256: string; samples: number; acknowledged?: true }
  >;
};
export interface SpoolSession {
  append(f: PcmFrame): void;
  flush(): Promise<void>;
  frames(fromSeq?: number, toSeq?: number): AsyncIterable<PcmFrame>;
  deleteRange(fromSeq: number, toSeq: number): Promise<void>;
  /** An optional PCM range commits with the metadata, or rejects without claiming a checkpoint. */
  setState(
    s: SpoolMeta['state'],
    patch?: Partial<SpoolMeta>,
    range?: { fromSeq: number; toSeq: number },
  ): Promise<void>;
  meta(): SpoolMeta;
  pcm(): Promise<Int16Array>;
  /** Backend commits, not a promise that a memory backend survives reload. */
  storageState(): 'pending' | 'stored' | 'failed';
  onStorageChange(listener: () => void): () => void;
}
/** IndexedDB database `poneva-speech-spool` v1, with a memory fallback (`durable: false`). */
export interface SpeechSpool {
  readonly durable: boolean;
  open(meta: Omit<SpoolMeta, 'updatedAt'>): Promise<SpoolSession>;
  list(owner: string, kind?: SpoolMeta['kind']): Promise<SpoolMeta[]>;
  remove(id: string): Promise<void>;
  purgeOwner(owner: string): Promise<void>;
  sweep(maxAgeMs: number): Promise<number>;
}

// ── Speech routes (C-3) ─────────────────────────────────────────────────────

export type SpeechUnavailableReason =
  | 'not_configured'
  | 'provider_unavailable'
  | 'speech_residency_unavailable'
  | 'processing_policy_unavailable';
/**
 * `speech` in GET /api/config. The client fails closed to
 * `{ available: false, reason: 'unknown', modes: all false }` when the field is
 * missing or malformed, and each mode to false when it is not a boolean.
 * `voice`, `handsFree` and `readAloud` (BUILD 2) are deployment and provider
 * level only; the company and person switches come with GET /speech/config.
 */
export type SpeechAvailability = {
  available: boolean;
  reason: SpeechUnavailableReason | 'unknown' | null;
  modes: {
    dictation: boolean;
    partials: boolean;
    meetingPreview: boolean;
    meetingRecord: boolean;
    voice: boolean;
    handsFree: boolean;
    readAloud: boolean;
  };
};
/** The fail-closed availability: missing, malformed or not yet loaded. */
export const SPEECH_UNAVAILABLE: SpeechAvailability = Object.freeze({
  available: false,
  reason: 'unknown',
  modes: {
    dictation: false,
    partials: false,
    meetingPreview: false,
    meetingRecord: false,
    voice: false,
    handsFree: false,
    readAloud: false,
  },
});
/**
 * GET /api/v1/speech/config (requires a user; no-store). There is no language
 * selector is required: engines detect the spoken language, with personal,
 * conversation and company hints resolved on the server.
 */
export type SpeechConfig = {
  available: boolean;
  reason: SpeechUnavailableReason | 'unknown' | null;
  limits: { maxSessionSec: number; idleStopSec: number; warnBeforeSec: number };
  /** BUILD 2 (C2-6). Absent from an older server: voice mode is unavailable. */
  voice?: VoiceConfig;
  /** BUILD 2 (C2-6). Absent from an older server: read-aloud is unavailable. */
  readAloud?: ReadAloudConfig;
};

export type SpeechMode = 'dictation' | 'meeting' | 'voice';
/** POST /api/v1/speech/sessions body. */
export type SpeechMintRequest = {
  mode: SpeechMode;
  /** Required when `mode` is 'voice' (C2-6). */
  style?: VoiceStyle;
  /** Required when `mode` is 'meeting': the meeting must be capturing and owned by the user. */
  meetingId?: string;
  /** Voice/dictation only: an opaque reference, read only when personally owned. */
  threadId?: string;
  /** The previous session after an unexpected close (C-4 S1 replay). */
  replayOf?: string;
};
export type SpeechLanguageSource =
  | 'explicit'
  | 'correction'
  | 'personal'
  | 'conversation'
  | 'company'
  | 'deployment'
  | 'auto';
/** POST /api/v1/speech/sessions → 201. */
export type SpeechMintResponse = {
  sessionId: string;
  /** Single-use, 60 s. Sent only as `?ticket=` on the WebSocket URL; never logged. */
  ticket: string;
  wsPath: string;
  expiresInSec: number;
  audio: { sampleRate: 16000; encoding: 'pcm_s16le'; frameMs: 100 };
  provider: { id: string; model: string };
  partials: boolean;
  languages: {
    mode: SpeechLanguageMode;
    codes: string[];
    pin: string | null;
    source: SpeechLanguageSource;
  };
  limits: { maxSessionSec: number; idleStopSec: number };
  /** false for meeting previews: the server holds no audio and never degrades to utterance. */
  holdsAudio: boolean;
  /**
   * Voice sessions only (C2-4): `commit` keeps the session open across turns;
   * `endpointing` is 'semantic' when the provider reports ends of turn.
   */
  voice?: { commit: true; endpointing: 'semantic' | 'none'; maxSessionSec: number };
};
/** ui/speech/transport/mint.ts: rejects with `SpeechHttpError`. */
export type MintSpeechSession = (req: SpeechMintRequest) => Promise<SpeechMintResponse>;

/** The voice-mode mint's 503 `voice_mode_unavailable{reason}` (C2-14). HTTP only. */
export const VOICE_MODE_UNAVAILABLE = 'voice_mode_unavailable';
export type VoiceModeUnavailableReason = 'not_configured' | 'policy_off' | 'no_allowlisted_engine';

/**
 * The HTTP error of every speech route. 401, 415 and 429 are mapped by
 * `status`; `code` decides only for 400, 403 and 503 (C-3).
 */
export type SpeechHttpErrorCode =
  | SpeechErrorCode
  | typeof VOICE_MODE_UNAVAILABLE
  | 'json_required'
  | 'request_failed';
export class SpeechHttpError extends Error {
  readonly status: number;
  readonly code: SpeechHttpErrorCode;
  readonly retryAfterSec: number | null;
  /** 503 only: why speech, or voice mode, is unavailable. */
  readonly reason: SpeechUnavailableReason | VoiceModeUnavailableReason | null;
  constructor(
    status: number,
    code: SpeechHttpErrorCode,
    retryAfterSec: number | null = null,
    reason: SpeechUnavailableReason | VoiceModeUnavailableReason | null = null,
  ) {
    super(code);
    this.name = 'SpeechHttpError';
    this.status = status;
    this.code = code;
    this.retryAfterSec = retryAfterSec;
    this.reason = reason;
  }
}

// ── Session socket (ui/speech/transport/session-socket.ts: class SpeechSocket) ─

/** `new SpeechSocket(options)`; the URL is `wsUrl(mint.wsPath) + '?ticket=' + mint.ticket`. */
export interface SpeechSocketOptions {
  url: string;
  onMessage(m: ServerMessage): void;
  onClose(code: number, reason: string): void;
}
/** The surface WP-4's live preview uses; WP-3's `SpeechSocket` implements it. */
export interface SpeechSocketApi {
  /** False when `bufferedAmount` exceeds the back-pressure mark: the frame stays in the spool. */
  sendFrame(f: PcmFrame, flags?: number): boolean;
  send(c: ClientControl): void;
  close(code?: number): void;
}
